import CACard from '@ds/components/containers/CACard'
import CAHeading from '@ds/components/headers/CAHeading'
import getScriptJson from '@vuejs/utils/getScriptJson'
import {
	computed,
	defineComponent,
	ref,
} from 'vue'

import { createVueApp } from '@/entrypoints/createVueApp'

import type {
	Cell,
	ColumnData,
	RowData,
} from '../components/shared/DataTable'
import MoveDataBetweenTables from '../components/shared/MoveDataBetweenTables'
import { usePackagesAndProgramsStore } from '../stores/PackagesAndProgramsStore'

const PackageManagementComponent = defineComponent({
	name: 'PackageManagementPage',

	setup() {
		const config: PackageManagementConfig = getScriptJson('package_management_vue_config')

		const packagesAndProgramsStore = usePackagesAndProgramsStore()

		packagesAndProgramsStore.courses = config.courses

		const cellDataArray = ref<string[]>([
			'name',
			'sku',
			'portal_name',
		])

		const columns = ref<ColumnData[]>([
			{
				ariaLabel: 'Sort by Name',
				columnId: 'name',
				isSortable: true,
				name: 'Name',
			},
			{
				ariaLabel: 'Sort by Sku',
				columnId: 'sku',
				isSortable: true,
				name: 'Sku',
			},
			{
				ariaLabel: 'Portal',
				columnId: 'portal',
				isSortable: true,
				name: 'Portal',
			},
		])

		const selectedRows = ref<RowData[]>([])

		const dataToCellDataArray = (course: Course): Cell[] => {
			return [
				{
					title: {
						dataTest: course.name,
						text: course.name,
					},
				},
				{
					badge: {
						style: 'filled',
						text: `${course.sku ?? ''}`,
						type: 'gray',
					},
					title: { text: '' },
				},
				{ title: { text: course.portal_name ?? '' } },
			]
		}

		const rows = computed(() => config.courses.map((course: Course) => ({
			cells: dataToCellDataArray(course),
			rowId: `${course.id}`,
		})))

		const updateSelectedRows = (data: RowData[]): void => {
			selectedRows.value.push(...data)
		}

		const removeFromSelectedRows = (data: RowData[]): void => {
			data.forEach(row => {
				const index = selectedRows.value.findIndex(selectedRow => selectedRow.rowId === row.rowId)

				if (index > -1) {
					selectedRows.value.splice(index, 1)
				}
			})
		}

		return {
			cellDataArray,
			columns,
			config,
			removeFromSelectedRows,
			rows,
			selectedRows,
			updateSelectedRows,
		}
	},

	render() {
		return (
			<div class="my-4">
				<CAHeading
					additionalClasses="mb-3"
					heading={5}
					title="Courses"
				/>

				<MoveDataBetweenTables
					buttonText="courses"
					cellDataArray={this.cellDataArray}
					columns={this.columns}
					data={this.config.courses}
					data-type="courses"
					filterText="Filter Courses"
					inputId="package_courses"
					itemsPerPage={5}
					rows={this.rows}
					selectedRows={this.selectedRows}
					onRemove={this.removeFromSelectedRows}
					onSelect={this.updateSelectedRows}
				/>

				<CACard title="Selected courses">
					<ul id="selected_courses" style="height: 100px; overflow-y: scroll;">
						{this.selectedRows.map(row => <li>{row.cells[0].title.text}</li>)}
					</ul>
				</CACard>
			</div>
		)
	},
})

export default function () {
	const app = createVueApp(PackageManagementComponent)

	app.mount('#packageManagementApp')
}

interface Course {
	id: number
	name: string
	portal_name: string
	sku: string
}

interface PackageManagementConfig {
	courses: (Course & { enabled: boolean })[]
}
